
import { useEffect } from 'react';
import Hey from "@/lib/hey";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";

export interface NotifPostAction {
  trip: string;
  userId: string;
  offerId: string;
  offerData?: any;
  type: string;
  params: { [key: string]: string };
};

const _PENDING_POST_ACTION = 'pendingPostAction';
export class FlutterUtils {

    static clearPostAction() {
      localStorage.removeItem(_PENDING_POST_ACTION);
    }

    // sample {"trip": "MAN1743666258500", "type": "activity"}
    static async getCurrenttNotifPostAction() : Promise<NotifPostAction> {
      try {
        let storedPostAction = localStorage.getItem(_PENDING_POST_ACTION);
        if (!storedPostAction || storedPostAction === '') {
          return null;
        }
        let postAction = JSON.parse(storedPostAction); 
        if (postAction.offerId) {
          postAction.offerData = await Hey.HeyUtils.getInstance().getOfferById(postAction.offerId);
        }
        return postAction;
      } catch (e) {
        console.error("Error while retrieving post action", e);
        this.clearPostAction();
      }
      return null;
    }

    static setTitle(title: string) {
      if (globalThis.Flutter) {
        console.debug("Updating flutter title:", title)
        globalThis.Flutter.postMessage(JSON.stringify({
            type: 'setTitle',
            title: title
        }));
      } else {
          console.debug("No flutter found")
      }
    }

    static storeTripForOffline(ref:string, trip: any) {
      if (globalThis.Flutter) {
        console.debug("Storing trip for offline mode :", trip)
        globalThis.Flutter.postMessage(JSON.stringify({
            type: 'offlineTrip',
            ref: ref,
            trip : JSON.stringify(trip)
        }));
      } else {
          console.debug("No flutter found")
      }
    }

    static clearOfflineTrips(refs:string[]) {
      if (globalThis.Flutter) {
          console.debug("Sending trip references still valid:", refs);
          globalThis.Flutter.postMessage(JSON.stringify({
              type: 'clearOfflineTrips',
              refs: refs,
          }));
        } else {
            console.debug("No flutter found")
        }
      }

    static setPostActionAndNavigate(postAction: NotifPostAction, navigate, path: any) {
      try {
        localStorage.setItem(_PENDING_POST_ACTION, JSON.stringify(postAction));
      } catch (e) {
        console.error("Error while storing post action", e);
        localStorage.clear();
        localStorage.setItem(_PENDING_POST_ACTION, JSON.stringify(postAction));
      }
      navigate(path);
    }

    static async requestFirebaseToken() {
        if (globalThis.Flutter) {
            console.debug("Calling flutter:", globalThis.Flutter)
            globalThis.Flutter.postMessage(JSON.stringify({
                type: 'getFbToken',
                data: {}
            }));
        } else {
            console.debug("No flutter found")
        }
    }

    static isFlutterWebapp() {
      return localStorage.getItem("isFlutterApp") === "Y";
    }


    static handleOfferNotification(notif, navigate) {
      let tripRef = notif.reference
      let userId = notif.userId
      let offerId = notif.id
      let offerProduct = notif.productId
      if (!userId || !tripRef || !offerId) {
        console.error("Nothing to execute in this notification", notif);
        return false;
      }

      let postAction:NotifPostAction = {
        trip: notif.reference,
        userId: notif.userId,
        offerId: notif.id,
        type: '',
        params: {}
      };

      if (notif.externalLink) {
        window.open(notif.externalLink, "_blank");
        return true;
      }
      
      let targetedPage = null;
      switch (offerProduct) {
        case 'product-mylittleadventure-reco-tours':
        case 'product-mylittleadventure-pre-trip-offer':
          postAction.type = 'activity';
          targetedPage = '/';
          break;
        case "product-Accuweather-ClientAPI-info":
        case "product-accuweather-info":
          postAction.type = 'weather';
          targetedPage = '/';
          break;
        case "product-loungepass-layover":
          postAction.type = 'lounge';
          targetedPage = '/';
          break;
        default:
          console.error("Unknown offer product type:", offerProduct);
          return false;
      }
      if (targetedPage) {
        this.setPostActionAndNavigate(postAction, navigate, targetedPage);
        return true;
      } else {
        console.error("No targeted page found for this notification");
      }
      return false;
    }

    static executeNotification(notif, navigate) {
      if (!notif || !notif.data) {
        console.debug("No notification data provided");
        return false;
      }
      console.log("Executing notification from Flutter:", JSON.stringify(notif.data, null, 2));
      switch (notif.data.type) {
        case 'trip':
          // navigate(`/trip/${message.data.tripId}`);
          break;
        case 'video':
          // navigate('/videos');
          break;
        case 'FlightStatusUpdate':
        case 'offer':
          return this.handleOfferNotification(notif.data, navigate);
          break;
        case 'profile':
          // navigate('/profile');
          break;
        case 'oag':
          // navigate('/upcoming-flights');
          break;
        // default:
        //  navigate('/');
      }
      return false;
    }

    static async requestPastNotifications(cb) {
        if (globalThis.Flutter) {
            globalThis.Flutter.postMessage(JSON.stringify({
                type: 'getPastNotifs',
                data: {}
            }));
            window.flutterBridge.pastNotifsUserCb = cb;
        } else {
            console.debug("No flutter found")
        }
    }
};

type FlutterBridgeParams = {
  route?: string;
} | null;

export const useFlutterBridge = (params: FlutterBridgeParams) => {
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (!window.flutterBridge) {
      console.log("Creating Flutter bridge");
    window.flutterBridge = {
      handleNotification: (message: any) => {
        console.log('Executing notification from Flutter:', message);
        FlutterUtils.executeNotification(message, navigate);
      },
      updateNotificationCount: (count: number) => {
        console.debug('Received notification count from Flutter:', count);
        localStorage.setItem('unreadNotificationCount', count.toString());
        window.dispatchEvent(new CustomEvent('notificationCountUpdated', { detail: { count } }));
        return true;
      },
      isUserAuthenticated: () => {
        console.debug("Checking authentication status...");
        const isAuth = Hey.HeyUtils.getInstance().isUserAuthenticated();
        console.debug("Is authenticated:", isAuth);
        if (!isAuth) {
          if (location.pathname.indexOf('auth') === -1) {
            console.log("User not authenticated, redirecting to auth page...");
            navigate('/auth');
          }
          return false;
        }
        return true;
      },
      setFirebaseToken: (token: string, platform: string) => {
        console.debug('Received setFirebaseToken request from flutter!');
        localStorage.setItem('isFlutterApp', "Y");
        if (!Hey.HeyUtils.getInstance().isUserAuthenticated()) {
          console.error('Cannot set firebase token: user not authenticated');
          return false;
        }
        Hey.HeyUtils.getInstance().updateUserPushToken(token, platform);
        return true 
      },
      pastNotifsUserCb: null,
      setPastNotifs: (notifs: any) => {
        console.debug("Received past notifications from Flutter in bridge");
        if (window.flutterBridge.pastNotifsUserCb) {
          window.flutterBridge.pastNotifsUserCb(notifs);
          window.flutterBridge.pastNotifsUserCb = null;
        }
      }
    };
  } else {
    console.debug("something bad happend");
  }
    return () => {
      // Only remove if this instance created it
      if (window.flutterBridge) {
        delete window.flutterBridge;
      }
    };
  }, [toast, navigate]);
};

declare global {
  interface Window {
    flutterBridge: {
      handleNotification: (data: any) => void;
      isUserAuthenticated: () => boolean;
      setFirebaseToken: (token: string, platform: string) => boolean;
      setPastNotifs: (notifs: any) => void;
      pastNotifsUserCb: (notifs: any) => void;
      updateNotificationCount: (count: number) => boolean;
    };
  }
}
